import { Message } from "src/layouts/AskDatatram/ChatInterface";
import { getApiBaseUrl, getChatbotUrl } from "src/config/endpoints";
import i18n from "src/i18n/config";
import { getApiLangFromI18n, normalizeApiLang, type ApiLang } from "src/utils/apiLang";
import { buildAuthHeaders, getAuthToken } from "./httpUtils";

const API_BASE_URL = getApiBaseUrl();
const CHATBOT_URL = getChatbotUrl();

const API_ENDPOINT_CHATBOT = `${CHATBOT_URL}/ask`;
const API_ENDPOINT_CHATBOT_IMAGE = `${CHATBOT_URL}/image`;
const API_ENDPOINT_CREATE_SESSION = `${API_BASE_URL}/chatbot/create-session`;
const CHATBOT_MESSAGE_NUMBER_KEY = "chatbot_message_number";
const CHAT_SESSION_ID_KEY = "session_id";
const DEFAULT_SESSION_HEADLINE = "AI Chat Session";

interface ImagePayload {
  name: string;
  type: string;
  dataUrl: string;
}

interface ChatbotRequestMetadata {
  first_message: 0 | 1;
}

const QUERY_LIMIT_REACHED_STATUS = 402;
const QUERY_LIMIT_REACHED_DETAIL =
  "Your query limit has been maximised, please contact DataTram Team";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  created_at: string;
  file_data?: string;
  file_url?: string;
  file_name?: string;
  file_type?: string;
  file?: {
    url?: string;
    name?: string;
    type?: string;
    file_data?: string;
    file_url?: string;
  };
}

const handleApiError = (error: unknown): never => {
  throw error instanceof Error ? error : new Error("Chatbot API request failed");
};

export const resetChatbotRequestMetadata = () => {
  localStorage.removeItem(CHATBOT_MESSAGE_NUMBER_KEY);
};

let chatSessionInitPromise: Promise<string> | null = null;
/**
 * Bumped whenever the chat is reset so a session created for an abandoned chat
 * is never written back over the fresh one.
 */
let chatSessionGeneration = 0;

/** Starts a fresh chat: drops the stored session and the per-session message counter. */
export const resetChatSession = () => {
  chatSessionGeneration += 1;
  chatSessionInitPromise = null;
  localStorage.removeItem(CHAT_SESSION_ID_KEY);
  resetChatbotRequestMetadata();
};

/**
 * A cached `session_id` survives language switches, so without this the
 * `lang` sent to `/chatbot/create-session` stays whatever it was the first
 * time a session was ever created for this browser. Dropping the session on
 * `languageChanged` forces the next message to open a new one with the
 * current language.
 */
i18n.on("languageChanged", () => {
  resetChatSession();
});

export const getActiveChatSessionId = (): string =>
  localStorage.getItem(CHAT_SESSION_ID_KEY) || "";

/** Switches the chat to an existing session (opened from history). */
export const setActiveChatSessionId = (sessionId: string) => {
  chatSessionGeneration += 1;
  chatSessionInitPromise = null;
  localStorage.setItem(CHAT_SESSION_ID_KEY, sessionId);
};

/** `POST /chatbot/create-session` answers with the raw id; tolerate a wrapped shape too. */
function extractSessionId(data: unknown): string {
  if (typeof data === "string") {
    return data.trim();
  }
  if (typeof data === "number") {
    return String(data);
  }
  if (data && typeof data === "object") {
    const sessionId = (data as Record<string, unknown>).session_id;
    if (typeof sessionId === "string") {
      return sessionId.trim();
    }
    if (typeof sessionId === "number") {
      return String(sessionId);
    }
  }
  return "";
}

/**
 * Creates an empty chat session on the backend so its `session_id` can be sent
 * with every `/ask` call. Without it the DS service has no key to scope
 * conversation memory to and parallel chat windows share the same history.
 */
const createChatSession = async (): Promise<string> => {
  const userId = Number(localStorage.getItem("user_id"));
  if (!userId) {
    throw new Error("User ID is required");
  }

  const apiKey = getAuthToken("api_key");
  const query = new URLSearchParams({
    lang: getApiLangFromI18n(i18n),
    api_key: apiKey,
  });

  const response = await fetch(`${API_ENDPOINT_CREATE_SESSION}?${query.toString()}`, {
    method: "POST",
    headers: buildAuthHeaders("api_key"),
    body: JSON.stringify({
      user_id: userId,
      session_headline: DEFAULT_SESSION_HEADLINE,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to create chat session: ${response.status}`);
  }

  const sessionId = extractSessionId(await response.json().catch(() => null));
  if (!sessionId) {
    throw new Error("Create chat session response did not contain a session id");
  }

  return sessionId;
};

/**
 * Returns the active `session_id`, creating one the first time it is needed
 * (new chat or page refresh). Concurrent senders share a single request so one
 * chat can never end up split across two sessions.
 */
const ensureChatSessionId = async (): Promise<string> => {
  const storedSessionId = localStorage.getItem(CHAT_SESSION_ID_KEY);
  if (storedSessionId) {
    return storedSessionId;
  }

  if (!chatSessionInitPromise) {
    const generation = chatSessionGeneration;
    chatSessionInitPromise = createChatSession()
      .then(sessionId => {
        if (generation === chatSessionGeneration) {
          localStorage.setItem(CHAT_SESSION_ID_KEY, sessionId);
        }
        return sessionId;
      })
      .finally(() => {
        chatSessionInitPromise = null;
      });
  }

  return chatSessionInitPromise;
};

/**
 * Session creation is best-effort: a failure must not block the user's message,
 * so the chat falls back to sending without a session id (as it did before).
 */
const resolveChatSessionId = async (): Promise<string> => {
  try {
    return await ensureChatSessionId();
  } catch {
    return getActiveChatSessionId();
  }
};

/**
 * `store-chat` and the chat-history APIs treat `session_id` as a number, so send
 * it as one. A non-numeric id is passed through untouched rather than becoming NaN.
 */
const toSessionIdPayload = (sessionId: string): number | string => {
  const numericSessionId = Number(sessionId);
  return Number.isFinite(numericSessionId) ? numericSessionId : sessionId;
};

const getChatbotRequestMetadata = (): ChatbotRequestMetadata => {
  const previousMessageNumber =
    Number(localStorage.getItem(CHATBOT_MESSAGE_NUMBER_KEY)) || 0;
  const messageNumber = previousMessageNumber + 1;

  localStorage.setItem(CHATBOT_MESSAGE_NUMBER_KEY, String(messageNumber));

  return {
    first_message: messageNumber === 1 ? 1 : 0,
  };
};

export const syncChatbotMessageNumber = (messageNumber: number) => {
  const normalizedMessageNumber = Math.max(0, Math.floor(messageNumber));
  localStorage.setItem(CHATBOT_MESSAGE_NUMBER_KEY, String(normalizedMessageNumber));
};

function coerceReplyString(val: unknown): string {
  if (val === null) {
    return "";
  }
  if (typeof val === "string") {
    return val;
  }
  if (typeof val === "number" || typeof val === "boolean") {
    return String(val);
  }
  return "";
}

/** Pull assistant text from common API shapes (flat or nested). */
function extractChatbotReplyText(data: unknown): string {
  if (!data || typeof data !== "object") {
    return "";
  }
  const d = data as Record<string, unknown>;
  const direct =
    coerceReplyString(d.response) ||
    coerceReplyString(d.message) ||
    coerceReplyString(d.answer) ||
    coerceReplyString(d.content) ||
    "";
  if (direct) {
    return direct;
  }
  const nested = d.data;
  if (nested && typeof nested === "object") {
    const n = nested as Record<string, unknown>;
    return coerceReplyString(n.response) || coerceReplyString(n.message) || "";
  }
  return "";
}

function extractChatbotQuestions(data: unknown): string[] {
  if (!data || typeof data !== "object") {
    return [];
  }
  const q = (data as Record<string, unknown>).questions;
  if (!Array.isArray(q)) {
    return [];
  }
  return q.filter((item): item is string => typeof item === "string");
}

function extractChatbotIntent(data: unknown): string[] {
  if (!data || typeof data !== "object") {
    return [];
  }
  const intent = (data as Record<string, unknown>).intent;
  if (!Array.isArray(intent)) {
    return [];
  }
  return intent.filter((item): item is string => typeof item === "string");
}

function extractChatbotMessageId(data: unknown): string | undefined {
  if (!data || typeof data !== "object") return undefined;
  const id = (data as Record<string, unknown>).message_id;
  return typeof id === "string" && id ? id : undefined;
}

function extractChatbotDetailMessage(data: unknown): string {
  if (!data || typeof data !== "object") {
    return "";
  }
  return coerceReplyString((data as Record<string, unknown>).detail).trim();
}

function extractChatName(data: unknown): string {
  if (!data || typeof data !== "object") {
    return "";
  }
  return coerceReplyString((data as Record<string, unknown>).chat_name).trim();
}

function salvageDetailStringFromRaw(raw: string): string {
  const trimmed = raw.trim();
  const m = trimmed.match(/"detail"\s*:\s*"((?:[^"\\]|\\.)*)/);
  if (!m?.[1]) {
    return "";
  }
  return m[1].replace(/\\"/g, "\"").replace(/\\n/g, "\n").replace(/\\r/g, "\r");
}

/**
 * If the body is truncated or not valid JSON, still try to recover `response` text
 * (e.g. `{"response":"Great, let's create an email` from a broken stream).
 */
function salvageResponseStringFromRaw(raw: string): string {
  const trimmed = raw.trim();
  const m = trimmed.match(/"response"\s*:\s*"((?:[^"\\]|\\.)*)/);
  if (!m?.[1]) {
    return "";
  }
  return m[1].replace(/\\"/g, "\"").replace(/\\n/g, "\n").replace(/\\r/g, "\r");
}

/**
 * Read JSON from /chat/ask (or image) even when HTTP status is not 2xx.
 * Some gateways return 401/403/502 with a usable JSON body; we still map `response` into the UI.
 */
async function parseChatbotAskResponse(response: Response): Promise<{
  replyText: string;
  questions: string[];
  intent: string[];
  messageId: string | undefined;
  detailText: string;
  chatName: string;
  httpOk: boolean;
  status: number;
}> {
  const raw = await response.text();
  let data: unknown = {};
  try {
    data = raw ? JSON.parse(raw) : {};
  } catch {
    const salvagedDetail = salvageDetailStringFromRaw(raw);
    if (salvagedDetail.trim()) {
      return {
        replyText: "",
        questions: [],
        intent: [],
        messageId: undefined,
        detailText: salvagedDetail,
        chatName: "",
        httpOk: response.ok,
        status: response.status,
      };
    }

    const salvaged = salvageResponseStringFromRaw(raw);
    if (salvaged.trim()) {
      return {
        replyText: salvaged,
        questions: [],
        intent: [],
        messageId: undefined,
        detailText: "",
        chatName: "",
        httpOk: response.ok,
        status: response.status,
      };
    }
    throw new Error(
      `Chatbot API returned non-JSON (${response.status}): ${raw.slice(0, 200)}`,
    );
  }
  return {
    replyText: extractChatbotReplyText(data),
    questions: extractChatbotQuestions(data),
    intent: extractChatbotIntent(data),
    messageId: extractChatbotMessageId(data),
    detailText: extractChatbotDetailMessage(data),
    chatName: extractChatName(data),
    httpOk: response.ok,
    status: response.status,
  };
}

export class ChatbotQueryLimitReachedError extends Error {
  status: number;
  backendDetail: string;

  constructor(backendDetail: string, status = QUERY_LIMIT_REACHED_STATUS) {
    super("Chatbot query limit reached");
    this.name = "ChatbotQueryLimitReachedError";
    this.status = status;
    this.backendDetail = backendDetail;
  }
}

export const isQueryLimitReachedDetail = (detail: string): boolean => {
  const normalized = detail.trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  if (normalized === QUERY_LIMIT_REACHED_DETAIL.toLowerCase()) {
    return true;
  }
  // Some environments incorrectly return 500 while preserving semantic detail.
  return normalized.includes("query limit") && normalized.includes("contact datatram");
};

const isQueryLimitReachedResponse = (status: number, detailText: string): boolean =>
  (status === QUERY_LIMIT_REACHED_STATUS || status >= 400) &&
  isQueryLimitReachedDetail(detailText);

const dataUrlToBlob = (dataUrl: string) => {
  const [meta, base64] = dataUrl.split(",");
  const mime = meta?.match(/data:(.*?);base64/)?.[1] || "application/octet-stream";
  const binary = atob(base64 || "");
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
};

export const sendMessageToAPI = async (
  textToSend: string,
  userId: string | null,
  tenantId: string,
  getTime: () => string,
  lang?: ApiLang
): Promise<{
  botMessage: Message;
  questions: string[];
  intent: string[];
  chatName: string;
  messageId: string | undefined;
}> => {
  const resolvedUserId = userId || localStorage.getItem("user_id");
  const resolvedTenantId = tenantId || localStorage.getItem("tenant_id") || "";

  if (!resolvedUserId) { throw new Error("User ID is required"); }
  if (!resolvedTenantId) { throw new Error("Tenant ID is required"); }

  // Scope the chatbot's conversation memory to this chat session so history
  // does not bleed across concurrent chats/windows for the same user. The
  // session is created up front when this is the chat's first message.
  const sessionId = await resolveChatSessionId();

  try {
    const chatbotMetadata = getChatbotRequestMetadata();
    // The backend needs the user's api_key in the payload (not just the auth
    // header) so it can call the segments/audiences APIs on their behalf.
    const apiKey = getAuthToken("api_key");
    const response = await fetch(API_ENDPOINT_CHATBOT, {
      method: "POST",
      headers: {
        ...buildAuthHeaders("api_key"),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: textToSend,
        user_id: resolvedUserId,
        tenant_id: resolvedTenantId,
        api_key: apiKey,
        lang: normalizeApiLang(lang),
        ...(sessionId ? { session_id: toSessionIdPayload(sessionId) } : {}),
        ...chatbotMetadata,
      }),
    });

    const { replyText, questions, intent, messageId, detailText, chatName, httpOk, status } = await parseChatbotAskResponse(response);

    if (isQueryLimitReachedResponse(status, detailText)) {
      throw new ChatbotQueryLimitReachedError(detailText, status);
    }

    if (!httpOk && !replyText.trim()) {
      throw new Error(`Chatbot API request failed: ${status} ${response.statusText}`);
    }

    const cleanedResponse = replyText
      .replace(/\$/g, "Rs. ")
      .replace(/\bdollars\b/gi, "rupees")
      .trim();

    const botMessage: Message = {
      sender: "bot",
      text:
        cleanedResponse ||
        (!httpOk ? `Something went wrong (${status}). Please try again.` : "Sorry, I didn't get that."),
      timestamp: getTime(),
    };

    return { botMessage, questions, intent, chatName, messageId };
  } catch (error) {
    return handleApiError(error);
  }
};

export const sendImageMessageToAPI = async (
  textToSend: string,
  userId: string | null,
  tenantId: string,
  image: ImagePayload,
  getTime: () => string,
  lang?: ApiLang
): Promise<{
  botMessage: Message;
  questions: string[];
  intent: string[];
  chatName: string;
  messageId: string | undefined;
}> => {
  const resolvedUserId = userId || localStorage.getItem("user_id");
  const resolvedTenantId = tenantId || localStorage.getItem("tenant_id") || "";

  if (!resolvedUserId) { throw new Error("User ID is required"); }
  if (!resolvedTenantId) { throw new Error("Tenant ID is required"); }

  // Scope the chatbot's conversation memory to this chat session so history
  // does not bleed across concurrent chats/windows for the same user. The
  // session is created up front when this is the chat's first message.
  const sessionId = await resolveChatSessionId();

  try {
    const chatbotMetadata = getChatbotRequestMetadata();
    // The backend needs the user's api_key in the payload (not just the auth
    // header) so it can call the segments/audiences APIs on their behalf.
    const apiKey = getAuthToken("api_key");
    const formData = new FormData();
    formData.append("query", textToSend);
    formData.append("user_id", resolvedUserId);
    formData.append("tenant_id", resolvedTenantId);
    formData.append("api_key", apiKey);    formData.append("lang", normalizeApiLang(lang));
    if (sessionId) {
      formData.append("session_id", sessionId);
    }
    formData.append("first_message", String(chatbotMetadata.first_message));
    formData.append("file", dataUrlToBlob(image.dataUrl), image.name);

    const response = await fetch(API_ENDPOINT_CHATBOT_IMAGE, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${apiKey}`,
      },
      body: formData,
    });

    const { replyText, questions, intent, messageId, detailText, chatName, httpOk, status } = await parseChatbotAskResponse(response);

    if (isQueryLimitReachedResponse(status, detailText)) {
      throw new ChatbotQueryLimitReachedError(detailText, status);
    }

    if (!httpOk && !replyText.trim()) {
      throw new Error(`Chatbot Image API request failed: ${status} ${response.statusText}`);
    }

    const cleanedResponse = replyText
      .replace(/\$/g, "Rs. ")
      .replace(/\bdollars\b/gi, "rupees")
      .trim();

    const botMessage: Message = {
      sender: "bot",
      text:
        cleanedResponse ||
        (!httpOk
          ? `Something went wrong (${status}). Please try again.`
          : "Sorry, I didn't get that."),
      timestamp: getTime(),
    };

    return { botMessage, questions, intent, chatName, messageId };
  } catch (error) {
    return handleApiError(error);
  }
};

export const fetchChatSuggestions = async (messageId: string): Promise<string[]> => {
  try {
    const response = await fetch(`${CHATBOT_URL}/suggestions/${messageId}`, {
      headers: buildAuthHeaders("api_key"),
    });
    if (!response.ok) return [];
    const data = await response.json();
    if (data?.status === "ready" && Array.isArray(data?.questions)) {
      return (data.questions as unknown[]).filter((q): q is string => typeof q === "string");
    }
    return [];
  } catch {
    return [];
  }
};

export async function storeChat({
  user_id,
  session_id,
  message,
  response,
  session_headline,
  lang,
  file_data,
  file_url,
  file_name,
  file_type,
}: {
  user_id: number;
  session_id: number;
  message: string;
  response: string;
  session_headline: string;
  lang?: ApiLang;
  file_data?: string | null;
  file_url?: string | null;
  file_name?: string | null;
  file_type?: string | null;
}) {
  let apiKey: string;
  try {
    apiKey = getAuthToken("api_key");
  } catch {
    return {};
  }

  try {
    const formData = new FormData();
    formData.append("user_id", String(user_id));
    formData.append("session_id", String(session_id));
    formData.append("message", message || "");
    formData.append("response", response || "");
    formData.append("session_headline", session_headline || "AI Chat Session");
    formData.append("lang", normalizeApiLang(lang));

    if (file_name) { formData.append("file_name", file_name); }
    if (file_type) { formData.append("file_type", file_type); }
    if (file_url) { formData.append("file_url", file_url); }

    if (file_data) {
      try {
        const blob = dataUrlToBlob(file_data);
        formData.append("image", blob, file_name || "image");
      } catch {
        formData.append("file_data", file_data);
      }
    }

    const res = await fetch(`${API_BASE_URL}/chatbot/store-chat`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${apiKey}`,
        Accept: "application/json",
      },
      body: formData,
    });

    return res.json().catch(() => ({}));
  } catch {
    return {};
  }
}

export interface ChatListItem {
  session_id: string;
  title: string;
  preview: string;
  created_at: string;
}

export const fetchChatList = async (): Promise<ChatListItem[]> => {
  try {
    const userId = localStorage.getItem("user_id");
    if (!userId) { throw new Error("User ID missing from localStorage"); }

    const apiKey = getAuthToken("api_key");
    const res = await fetch(`${API_BASE_URL}/chatbot/chat-list`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({ user_id: Number(userId) }),
    });

    if (!res.ok) { throw new Error(`API error: ${res.status}`); }

    const data = await res.json();
    return (data.sessions || []).map(
      (chat: { session_id: number | string; session_headline?: string; created_at: string }) => ({
        session_id: String(chat.session_id),
        title: chat.session_headline || "Untitled Chat",
        preview: "",
        created_at: chat.created_at,
      })
    );
  } catch {
    return [];
  }
};

export interface DeleteChatResponse {
  status: boolean;
  message: string;
  session_id?: number;
}

/**
 * Permanently delete a chat session from the user's history (server-side).
 * `DELETE /chatbot/chat-session` with `{ user_id, session_id }`.
 */
export const deleteChat = async (
  sessionId: number | string
): Promise<DeleteChatResponse> => {
  try {
    const userId = localStorage.getItem("user_id");
    if (!userId) { throw new Error("User ID missing from localStorage"); }

    const apiKey = getAuthToken("api_key");
    const res = await fetch(`${API_BASE_URL}/chatbot/chat-session`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        user_id: Number(userId),
        session_id: Number(sessionId),
      }),
    });

    const data = await res.json().catch(() => ({}));
    const backendMessage = typeof data?.message === "string" ? data.message : "";

    if (!res.ok) {
      return {
        status: false,
        message: backendMessage || `Failed to delete chat (${res.status})`,
      };
    }

    return {
      status: typeof data?.status === "boolean" ? data.status : true,
      message: backendMessage || "Chat deleted successfully",
      session_id: typeof data?.session_id === "number" ? data.session_id : undefined,
    };
  } catch (error) {
    console.error("Failed to delete chat:", error);
    return {
      status: false,
      message: error instanceof Error ? error.message : "Failed to delete chat",
    };
  }
};

export const fetchChatMessages = async (
  userId: number | string,
  sessionId: number | string
): Promise<ChatMessage[]> => {
  try {
    const apiKey = getAuthToken("api_key");
    const res = await fetch(`${API_BASE_URL}/chatbot/chat-messages?api_key=${apiKey}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        user_id: Number(userId),
        session_id: Number(sessionId),
      }),
    });

    if (!res.ok) { throw new Error(`API error: ${res.status}`); }

    const data = await res.json();
    return data.messages || [];
  } catch {
    return [];
  }
};

export const fetchChatImage = async (messageId: number | string): Promise<Blob | null> => {
  try {
    const apiKey = getAuthToken("api_key");
    const url = `${API_BASE_URL}/chatbot/chat-image/${messageId}?api_key=${apiKey}`;

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${apiKey}`,
        Accept: "application/json, image/*",
      },
    });

    if (!response.ok) {
      return null;
    }

    return response.blob();
  } catch {
    return null;
  }
};

export const getChatImageUrl = (messageId: number | string): string => {
  try {
    const apiKey = getAuthToken("api_key");
    return `${API_BASE_URL}/chatbot/chat-image/${messageId}?api_key=${apiKey}`;
  } catch {
    return "";
  }
};
